import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000, // 10 second timeout (reduced from 15)
  headers: {
    "Content-Type": "application/json",
  },
});

export const TOKEN_KEY = "ot_token";
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);
export const onUnauthorized = { handler: null };

// Retry configuration - faster retries
const MAX_RETRIES = 2; // Reduced from 3
const RETRY_DELAY = 500; // 500ms (reduced from 1000)

// Helper to delay execution
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Check if error is retryable (network errors, timeouts, 5xx errors)
const isRetryableError = (error) => {
  // Don't retry if request was cancelled
  if (error.name === 'CanceledError' || error.name === 'AbortError') {
    return false;
  }
  if (!error.response) {
    // Network error or timeout
    return true;
  }
  // Retry on 5xx server errors
  return error.response.status >= 500;
};

// Request interceptor for logging and connection state
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add timestamp to prevent caching issues
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry cancelled requests
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      return Promise.reject(error);
    }

    // Session missing/expired: go back to the access screen
    if (error.response?.status === 401 && !config?.url?.includes("/auth/login")) {
      clearToken();
      if (onUnauthorized.handler) onUnauthorized.handler();
      return Promise.reject(error);
    }
    
    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;
    
    // Check if we should retry
    if (config.__retryCount < MAX_RETRIES && isRetryableError(error)) {
      config.__retryCount += 1;
      
      console.warn(
        `Request failed, retrying (${config.__retryCount}/${MAX_RETRIES}):`,
        config.url
      );
      
      // Wait before retrying (shorter delay)
      await delay(RETRY_DELAY);
      
      // Retry the request
      return api(config);
    }
    
    // If all retries failed, show user-friendly message
    if (!error.response) {
      // Network error
      toast.error("Error de conexión. Verifique su internet e intente nuevamente.", {
        duration: 4000,
      });
    } else if (error.response.status >= 500) {
      toast.error("El servidor no responde. Intente nuevamente en unos segundos.", {
        duration: 4000,
      });
    }
    
    return Promise.reject(error);
  }
);

// Health check function to verify connection
export const checkHealth = async () => {
  try {
    const response = await api.get("/health", { timeout: 3000 });
    return response.data.status === "healthy";
  } catch (error) {
    console.error("Health check failed:", error);
    return false;
  }
};

// Wake up the connection (useful after inactivity)
export const wakeUpConnection = async () => {
  try {
    await checkHealth();
    return true;
  } catch (error) {
    return false;
  }
};

export default api;
